import type { ComponentPropsWithRef, MouseEvent, ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The eleven jobs a button does in this product.
 *
 * Chosen from the jobs rather than from the shapes: the tree drew a hundred and
 * fourteen buttons a hundred and fourteen ways, thirty-three of them distinct,
 * across seven text sizes, five heights and five radii. A primitive that grew a
 * variant per shape would have absorbed that sprawl instead of removing it, so
 * some sites change appearance. That is the point of the slice.
 *
 * Green stays its own variant rather than folding into `primary`, because in
 * this product green means the attorney has affirmed something, which is a
 * different act from submitting a form. A card offering "Confirm" beside
 * "Amend" draws both at once, and the two must not read as the same act.
 */
const VARIANT = {
  /** The one commit action on a screen. */
  primary: "border-primary bg-primary text-white hover:brightness-95",
  /** The attorney standing behind something. */
  confirm: "border-success bg-success text-white hover:brightness-95",
  /** Amend, edit, view, cancel, run again. */
  secondary: "border-border bg-card text-foreground hover:bg-secondary",
  /**
   * The way back out beside a popover form's act: bordered as `secondary`, in
   * words as quiet as `quiet`, so the act beside it is the one that reads.
   */
  tertiary:
    "border-border bg-transparent text-muted-foreground hover:bg-secondary hover:text-foreground",
  /** Dismiss, undo, show more: the quietest thing on a card. */
  quiet:
    "border-transparent text-muted-foreground hover:bg-secondary hover:text-foreground",
  /** The act that destroys, inside the question that asked first. */
  danger: "border-destructive bg-destructive text-white hover:brightness-95",
  /**
   * Remove, delete, on a row or a card: red words on the card's own ground, so
   * the trigger reads as a way out rather than the page's act. What it opens
   * asks first, and the act in there is `danger`.
   */
  "danger-outline":
    "border-destructive-border bg-card text-destructive hover:bg-destructive-surface",
  /**
   * A control on the shell's dark rail: pale words in a faint box, ringed in
   * white, since the violet focus ring is lost on the rail. Pressed, as a
   * header toggle whose panel is open, the box fills and the words go white.
   */
  shell:
    "border-white/15 bg-white/[0.06] text-sidebar-foreground hover:bg-white/10 hover:text-white focus-visible:ring-white/90 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar aria-pressed:border-white/35 aria-pressed:bg-white/20 aria-pressed:text-white",
  /** Another reading of the same page, as brand words with no box: "Collapse to independent claims". */
  link: "h-auto border-transparent px-0 text-primary hover:underline",
  /** A way to a page a row names, or back, in brand words: "Go to Invention", "Restore". */
  way: "border-border bg-card text-primary hover:bg-secondary",
  /** A page's walkthrough beside its title: help, not the page's act. */
  guide:
    "border-border bg-card text-primary shadow-[0_1px_2px_rgba(15,0,27,0.06)] hover:bg-secondary",
  /** The one hand-over a finished stage makes, in the brand's lighter violet: Download on a ready draft. */
  brand:
    "border-brand-mid bg-brand-mid text-white shadow-[0_6px_18px_rgba(131,83,253,0.35)] hover:brightness-110",
  /** The product asking about itself, in a magenta no step of a draft wears. */
  accent:
    "border-accent bg-accent text-accent-foreground shadow-accent hover:shadow-accent-raised hover:brightness-[1.08] aria-expanded:shadow-accent-raised aria-expanded:brightness-[1.08]",
} as const;

/**
 * Two, not five: one for a dense row and one for everything else.
 *
 * Both are the design's own button, the one its screens draw seventy times: a
 * control's radius rather than the card's, and semibold type a size under the
 * text around it. A pill-round button at the dense height read as a chip.
 */
const SIZE = {
  default: "h-8 rounded-control px-3 text-[12px]",
  dense: "h-7 rounded-control px-2.5 text-[11px]",
  /**
   * The one act a page is for, heading it at the page's scale: a list of clients
   * opens on adding one, a client's page on starting a draft. Never inside a card
   * or a modal, where the default holds.
   */
  lead: "h-auto gap-2 rounded-md px-4 py-2.5 text-[13px] shadow-[0_2px_6px_rgba(15,0,27,0.12)] active:scale-[0.98] [&_svg]:size-[15px]",
  /** The one act an upload card is for, large at its foot: Upload on an Intake slot. */
  card: "h-auto gap-2 rounded-md px-[18px] py-[11px] text-[15.5px] active:scale-[0.98] [&_svg]:size-4",
  /** One of a few answers to a question, round and pressed when chosen: a supporting file's tag. */
  option: "h-7 rounded-full px-3 text-[11.5px]",
  /** Round-ended, for the shell's dark bar, where the violet focus ring is lost. */
  pill: "h-7 gap-[7px] rounded-full px-[13px] text-[11.5px] focus-visible:ring-white/90 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar [&_svg]:size-[13px]",
  /** The one act a whole page leads to, at the page's largest: Download on a ready draft. */
  hero: "h-auto gap-3 rounded-[12px] px-8 py-[15px] text-[17px] active:scale-[0.98] [&_svg]:size-5",
  /** A popover form's Cancel, Confirm and Done, a size of their own as the modal's are. */
  foot: "h-auto rounded-inset px-4 py-2 text-detail",
  field: "h-[38px] rounded-md px-[15px] text-detail",
} as const;

const ICON_SIZE: Record<ButtonSize, string> = {
  default: "size-8 rounded-control",
  dense: "size-7 rounded-control",
  lead: "size-10 rounded-md",
  card: "size-11 rounded-md",
  option: "size-7 rounded-full",
  pill: "size-7 rounded-full",
  foot: "size-[34px] rounded-inset",
  hero: "size-14 rounded-[12px]",
  field: "size-[38px] rounded-md",
};

/** A bare quiet icon is drawn lighter than quiet words: it needs 3:1, not 4.5:1. */
const ICON_VARIANT: Partial<Record<ButtonVariant, string>> = {
  quiet:
    "border-transparent text-subtle-foreground hover:bg-secondary hover:text-foreground",
};

export type ButtonVariant = keyof typeof VARIANT;
export type ButtonSize = keyof typeof SIZE;

/**
 * Every variant carries a border, including the filled ones. Without it a
 * filled button and an outline button beside it are a pixel apart in height and
 * sit on different baselines, which is a thing the tree used to do. One held
 * with `aria-disabled` is drawn as a disabled one but keeps the keyboard.
 */
const SHARED =
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap border font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0";

interface Chrome {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
}

export type ButtonProps = Chrome &
  Omit<ComponentPropsWithRef<"button">, keyof Chrome>;

/**
 * The chrome on its own, for something that is a button to the eye and not one
 * to the browser: a link that navigates, or a dialog primitive that renders its
 * own action element.
 */
export function buttonChrome({
  variant = "primary",
  size = "default",
}: Pick<Chrome, "variant" | "size"> = {}): string {
  return cn(SHARED, "[&_svg]:size-4", SIZE[size], VARIANT[variant]);
}

/** A held button in a form must not submit it, so its press is cancelled rather than ignored. */
function preventDefault(event: MouseEvent<HTMLButtonElement>) {
  event.preventDefault();
}

export function Button({
  variant,
  size,
  held = false,
  className,
  children,
  onClick,
  ...rest
}: ButtonProps & {
  /** An act that cannot run yet: drawn disabled and announced so, while it keeps its focus and its name. */
  held?: boolean;
}) {
  return (
    <button
      className={cn(buttonChrome({ variant, size }), className)}
      aria-disabled={held || undefined}
      onClick={held ? preventDefault : onClick}
      {...rest}
    >
      {children}
    </button>
  );
}

/**
 * The icon-only control, on the same eleven variants.
 *
 * Square rather than padded, and it asks for its own label: an icon with no
 * words says nothing to a screen reader, and the tree had bordered icon
 * controls that said nothing at all.
 */
export type IconButtonProps = Chrome & {
  "aria-label": string;
} & Omit<ComponentPropsWithRef<"button">, keyof Chrome>;

export function IconButton({
  variant = "secondary",
  size = "default",
  className,
  children,
  ...rest
}: IconButtonProps) {
  return (
    <button
      className={cn(
        SHARED,
        "[&_svg]:size-3.5",
        ICON_SIZE[size],
        ICON_VARIANT[variant] ?? VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/** The names themselves, so a test can assert the set rather than a sample of it. */
export const BUTTON_VARIANTS = Object.keys(VARIANT) as readonly ButtonVariant[];
export const BUTTON_SIZES = Object.keys(SIZE) as readonly ButtonSize[];
